'use client';

import * as React from 'react';
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Input } from 'app/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from 'app/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from 'app/components/ui/avatar';
import { useInventorySlice } from 'app/pages/InventoryManagement/slice';
import { Product } from 'types/inventory';

export function DataTable() {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [search, setSearch] = React.useState('');

  // Use the inventory slice to inject reducers and get hooks
  const { useGetInventoryOverviewQuery } = useInventorySlice();

  // Fetch inventory data from API
  const {
    data: inventoryData,
    isLoading,
    error,
  } = useGetInventoryOverviewQuery({});

  // TODO: Re-enable API call once store is properly configured
  // const {
  //   data: inventoryData,
  //   isLoading,
  //   error,
  // } = useGetInventoryOverviewQuery({}, {
  //   skip: false, // Ensure the query runs
  // });

  // Combine all products from different categories
  const data = React.useMemo(() => {
    if (!inventoryData) return [];

    const allProducts = [
      ...(inventoryData.low_stock_products || []),
      ...(inventoryData.out_of_stock_products || []),
    ];

    // Remove duplicates based on product_id
    const uniqueProducts = allProducts.filter(
      (product, index, self) =>
        index === self.findIndex(p => p.product_id === product.product_id),
    );

    return uniqueProducts;
  }, [inventoryData]);

  const columns: ColumnDef<Product>[] = [
    {
      accessorKey: 'product_name',
      header: () => <span>Product</span>,
      cell: ({ row }) => (
        <div className="flex items-center space-x-4">
          <Avatar className="w-12 h-12">
            <AvatarImage
              src="/images/default-user-image.png"
              alt={row.original.product_name}
            />
            <AvatarFallback>
              {row.original.product_name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{row.original.product_name}</div>
            <div className="text-sm text-gray-500">
              {row.original.supplier || 'No supplier'}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'sku',
      header: () => <span>SKU</span>,
      cell: ({ row }) => <div>{row.getValue('sku')}</div>,
    },
    {
      accessorKey: 'stock_quantity',
      header: () => <span>Available</span>,
      cell: ({ row }) => (
        <Input
          type="number"
          defaultValue={row.original.stock_quantity}
          className="w-16 text-center"
        />
      ),
    },
    {
      accessorKey: 'sale_price',
      header: () => <span>Price</span>,
      cell: ({ row }) => <div>${row.original.sale_price}</div>,
    },
    {
      accessorKey: 'status',
      header: () => <span>Status</span>,
      cell: ({ row }) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${row.original.status === 1
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
            }`}
        >
          {row.original.status === 1 ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    state: { sorting },
  });

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full bg-white dark:bg-transparent p-4 rounded-lg shadow mt-4">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading inventory...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full bg-white dark:bg-transparent p-4 rounded-lg shadow mt-4">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <p className="text-red-600 font-semibold">
              API Server Not Available
            </p>
            <p className="text-sm text-gray-500 mt-1">
              The inventory API server is not running on port 3005.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Expected endpoint:
              http://localhost:3005/api/v1/admin/inventory/overview
            </p>
            <details className="mt-4 text-xs text-gray-400">
              <summary>Error details</summary>
              <pre className="mt-1 text-left">
                {JSON.stringify(error, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white dark:bg-transparent p-4 rounded-lg shadow mt-4">
      <div className="flex justify-between items-center">
        {/* Tabs */}
        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search Input */}
        <div className="flex items-center">
          <Input
            placeholder="Searching all inventory"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border mt-4">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-center">
                    <p className="text-gray-500">
                      No inventory data available.
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Please add some products to see inventory data.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
